import { isUndefined } from 'lodash';
import { ReactNode, createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { SpotterFamily } from '@/common/types';

export type SpotterContextType = {
    spotterId: string | null;
    viaOceansApi: boolean;
    spotterFamily: SpotterFamily;
};

const DEFAULT_CONTEXT_FINGERPRINT_KEY = '_default';

const defaultSpotterContextData = {
    spotterId: null,
    viaOceansApi: false,
    spotterFamily: 'mine',
    [DEFAULT_CONTEXT_FINGERPRINT_KEY]: true,
} as const;

const SpotterContext = createContext(defaultSpotterContextData as SpotterContextType);

export const useSpotterContext = () => {
    const context = useContext(SpotterContext);
    if (DEFAULT_CONTEXT_FINGERPRINT_KEY in context) {
        throw new Error('useSpotterContext called outside of any Spotter context');
    }
    return context;
};

/**
 * Returns a spotterId either from an argument or the context. This is intended to be used from common hooks; a top-level component should never need to use
 * it. The intention is for a hook to be able to take an explicit spotterId as an argument, but generally use the context-supplied ID by default.
 *
 * Because we don't want to accidentally catch a context spotterId in a spot where we're intending to pass one in explicitly, you must pass spotterId=null if
 * you're going to pass a spotterId but it's not yet available. It is an error to pass spotterId=undefined unless within a SpotterContext.Provider.
 */
export const useOptionalSpotterContext = ({
    spotterId,
    viaOceansApi = false,
    spotterFamily = 'mine',
}: { spotterId?: string | null; viaOceansApi?: boolean; spotterFamily?: SpotterFamily } = {}): SpotterContextType => {
    // deliberately not using useSpotterContext because it throws outside of a defined context
    const context = useContext(SpotterContext);
    if (!isUndefined(spotterId)) {
        return { spotterId, viaOceansApi, spotterFamily };
    }
    if (DEFAULT_CONTEXT_FINGERPRINT_KEY in context) {
        throw new Error(
            'spotterId was not supplied via argument or context. If you intend to supply a spotterId argument once available, pass null rather than undefined.'
        );
    }
    return context;
};

export const SpotterRouteParamContextProvider = ({
    children,
    viaOceansApi = false,
    spotterFamily = 'mine',
}: {
    children: ReactNode;
    viaOceansApi?: boolean;
    spotterFamily?: SpotterFamily;
}) => {
    const { spotterId = null } = useParams<{ spotterId: string }>();

    const contextValue = useMemo(
        () => ({ spotterId, viaOceansApi, spotterFamily }),
        [spotterId, viaOceansApi, spotterFamily]
    );

    return <SpotterContext.Provider value={contextValue}>{children}</SpotterContext.Provider>;
};

export const SpotterContextProvider = ({
    children,
    spotterId,
    viaOceansApi = false,
    spotterFamily = 'mine',
}: {
    children: ReactNode;
    spotterId: string | null;
    viaOceansApi?: boolean;
    spotterFamily?: SpotterFamily;
}) => {
    const contextValue = useMemo(
        () => ({ spotterId, viaOceansApi, spotterFamily }),
        [spotterId, viaOceansApi, spotterFamily]
    );

    return <SpotterContext.Provider value={contextValue}>{children}</SpotterContext.Provider>;
};
